import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck, DownloadFileFn} from '../../helpers'
import {
  SearchCheckoutByStaffIdClient,
  SalaryPaymentCheckoutClient,
  StaffPaymentCheckoutClient,
  DeleteCheckoutClient,
  GetCheckoutClient,
  AccountToAccountTransactionCheckoutClient,
  CreditTransactionCheckoutClient,
  DebitTransactionCheckoutClient,
  ExpenseInvoicePayCheckCheckoutClient,
  ExpenseInvoicePayMoneyCheckoutClient,
  ExpenseInvoicePayNewCheckCheckoutClient,
  SaleInvoiceReceiveCheckCheckoutClient,
  SaleInvoiceReceiveMoneyCheckoutClient,
  CompanyCollectionCheckoutClient,
  CompanyPaymentCheckoutClient,
  CompanyPayNewCheckCheckoutClient,
  CompanyPayCheckCheckoutClient,
  UpdateAdvanceCheckClient,
  SearchTransactionsCheckoutClient,
  GetCollectionReceiptClient,
  UpdateCheckoutDescriptionClient,
  SearchCheckoutApproveClient,
  UpdateCheckoutApproveConfirmationClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

const searchCheckoutByStaffIdClient = new SearchCheckoutByStaffIdClient(SERVICE_URL)
const salaryPaymentCheckoutClient = new SalaryPaymentCheckoutClient(SERVICE_URL)
const staffPaymentCheckoutClient = new StaffPaymentCheckoutClient(SERVICE_URL)
const deleteCheckoutClient = new DeleteCheckoutClient(SERVICE_URL)
const getCheckoutClient = new GetCheckoutClient(SERVICE_URL)
const accountToAccountTransactionCheckoutClient = new AccountToAccountTransactionCheckoutClient(
  SERVICE_URL
)
const creditTransactionCheckoutClient = new CreditTransactionCheckoutClient(SERVICE_URL)
const debitTransactionCheckoutClient = new DebitTransactionCheckoutClient(SERVICE_URL)
const expenseInvoicePayCheckCheckoutClient = new ExpenseInvoicePayCheckCheckoutClient(SERVICE_URL)
const expenseInvoicePayMoneyCheckoutClient = new ExpenseInvoicePayMoneyCheckoutClient(SERVICE_URL)
const expenseInvoicePayNewCheckCheckoutClient = new ExpenseInvoicePayNewCheckCheckoutClient(
  SERVICE_URL
)
const saleInvoiceReceiveCheckCheckoutClient = new SaleInvoiceReceiveCheckCheckoutClient(SERVICE_URL)
const saleInvoiceReceiveMoneyCheckoutClient = new SaleInvoiceReceiveMoneyCheckoutClient(SERVICE_URL)
const companyCollectionCheckoutClient = new CompanyCollectionCheckoutClient(SERVICE_URL)
const companyPaymentCheckoutClient = new CompanyPaymentCheckoutClient(SERVICE_URL)
const companyPayNewCheckCheckoutClient = new CompanyPayNewCheckCheckoutClient(SERVICE_URL)
const companyPayCheckCheckoutClient = new CompanyPayCheckCheckoutClient(SERVICE_URL)
const updateAdvanceCheckClient = new UpdateAdvanceCheckClient(SERVICE_URL)
const searchTransactionsCheckoutClient = new SearchTransactionsCheckoutClient(SERVICE_URL)
const getCollectionReceiptClient = new GetCollectionReceiptClient(SERVICE_URL)
const updateCheckoutDescriptionClient = new UpdateCheckoutDescriptionClient(SERVICE_URL)
const searchCheckoutApproveClient = new SearchCheckoutApproveClient(SERVICE_URL)
const updateCheckoutApproveConfirmationClient = new UpdateCheckoutApproveConfirmationClient(
  SERVICE_URL
)

const initialState = {
  loading: false,
  isRedirect: false,
  error: null,
  isDeleted: false,
  isArchived: false,
  transaction: false,
  companyNewCheck: false,
  companyCheckCheckout: false,
  excel: null,
  receipt: null,
  checkoutApproveConfirmState: null,
  updateDescription: false,
  checkout: null,
  detail: null,
  transactions: null,
  checkoutCreated: false,
}

// Async Thunks
export const checkoutSearchByStaff = createAsyncThunk(
  'checkout/searchByStaff',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await searchCheckoutByStaffIdClient.search(params)
      dispatch(setCheckoutList(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutSalaryPayment = createAsyncThunk(
  'checkout/salaryPayment',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await salaryPaymentCheckoutClient.create(params)
      dispatch(setCheckoutCreated(response || params || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutStaffPayment = createAsyncThunk(
  'checkout/staffPayment',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await staffPaymentCheckoutClient.create(params)
      dispatch(setCheckoutCreated(response || params || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutGetId = createAsyncThunk('checkout/getById', async (id, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await getCheckoutClient.get(id)
    dispatch(setCheckoutDetail(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const checkoutDelete = createAsyncThunk('checkout/delete', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await deleteCheckoutClient.delete(params)
    dispatch(setDeleted(params || true))
    dispatch(setTransaction(params || true))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const checkoutAccountToAccountTransaction = createAsyncThunk(
  'checkout/accountToAccountTransaction',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await accountToAccountTransactionCheckoutClient.create(params)
      dispatch(setTransaction(response || params || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutCreditTransaction = createAsyncThunk(
  'checkout/creditTransaction',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await creditTransactionCheckoutClient.create(params)
      dispatch(setTransaction(response || params || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutDebitTransaction = createAsyncThunk(
  'checkout/debitTransaction',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await debitTransactionCheckoutClient.create(params)
      dispatch(setTransaction(response || params || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutExpenseInvoicePayCheck = createAsyncThunk(
  'checkout/expenseInvoicePayCheck',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await expenseInvoicePayCheckCheckoutClient.create(params)
      dispatch(setTransaction(response || params || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutExpenseInvoiceAdvanceCheck = createAsyncThunk(
  'checkout/expenseInvoiceAdvanceCheck',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await updateAdvanceCheckClient.update(params)
      dispatch(setTransaction(response || params || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutExpenseInvoicePayMoney = createAsyncThunk(
  'checkout/expenseInvoicePayMoney',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await expenseInvoicePayMoneyCheckoutClient.create(params)
      dispatch(setTransaction(response || params || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutExpenseInvoicePayNewCheck = createAsyncThunk(
  'checkout/expenseInvoicePayNewCheck',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await expenseInvoicePayNewCheckCheckoutClient.create(params)
      dispatch(setTransaction(response || params || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutSaleInvoiceReceiveCheck = createAsyncThunk(
  'checkout/saleInvoiceReceiveCheck',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await saleInvoiceReceiveCheckCheckoutClient.create(params)
      dispatch(setTransaction(response || params || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutSaleInvoiceReceiveMoney = createAsyncThunk(
  'checkout/saleInvoiceReceiveMoney',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await saleInvoiceReceiveMoneyCheckoutClient.create(params)
      dispatch(setTransaction(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutCompanyCollection = createAsyncThunk(
  'checkout/companyCollection',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await companyCollectionCheckoutClient.create(params)
      dispatch(setTransaction(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutCompanyPayment = createAsyncThunk(
  'checkout/companyPayment',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await companyPaymentCheckoutClient.create(params)
      dispatch(setTransaction(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutCompanyPayNewCheck = createAsyncThunk(
  'checkout/companyPayNewCheck',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await companyPayNewCheckCheckoutClient.create(params)
      dispatch(setCompanyNewCheck(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutCompanyPayCheckout = createAsyncThunk(
  'checkout/companyPayCheckout',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await companyPayCheckCheckoutClient.create(params)
      dispatch(setCompanyCheckCheckout(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutTransactionsSearch = createAsyncThunk(
  'checkout/transactionsSearch',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await searchTransactionsCheckoutClient.search(
        params.name,
        params.beginDate,
        params.endDate,
        params.toAccountId,
        params.fromAccountId,
        params.minAmount,
        params.maxAmount,
        params.checkoutProcessTypeId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setTransactions(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutTransactionsExcel = createAsyncThunk(
  'checkout/transactionsExcel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await searchTransactionsCheckoutClient.excel(
        params.name,
        params.beginDate,
        params.endDate,
        params.toAccountId,
        params.fromAccountId,
        params.minAmount,
        params.maxAmount,
        params.checkoutProcessTypeId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutGetCollectionReceipt = createAsyncThunk(
  'checkout/getCollectionReceipt',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await getCollectionReceiptClient.get(params)
      if (response.data) {
        DownloadFileFn(response.data, params.fileName)
      }
      dispatch(setReceipt(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutUpdateDescription = createAsyncThunk(
  'checkout/updateDescription',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await updateCheckoutDescriptionClient.update(params)
      dispatch(setUpdateDescription(response || params || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutApproveGetAll = createAsyncThunk(
  'checkout/approveGetAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await searchCheckoutApproveClient.search(
        params.checkoutApproveStateId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setCheckoutList(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkoutApproveConfirm = createAsyncThunk(
  'checkout/approveConfirm',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await updateCheckoutApproveConfirmationClient.update(params)
      dispatch(setCheckoutApproveConfirm(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

// Slice
const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setExcel: (state, action) => {
      state.excel = action.payload
      state.loading = false
    },
    setCheckoutList: (state, action) => {
      state.checkout = action.payload
      state.loading = false
    },
    setCheckoutDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
    },
    setTransaction: (state, action) => {
      state.transaction = action.payload || true
      state.loading = false
    },
    setDeleted: (state) => {
      state.isDeleted = true
      state.loading = false
    },
    setCheckoutCreated: (state, action) => {
      state.checkoutCreated = action.payload || true
      state.isRedirect = action.payload
      state.loading = false
    },
    setCompanyNewCheck: (state, action) => {
      state.companyNewCheck = action.payload || true
      state.loading = false
    },
    setCompanyCheckCheckout: (state, action) => {
      state.companyCheckCheckout = action.payload || true
      state.loading = false
    },
    setTransactions: (state, action) => {
      state.transactions = action.payload
      state.loading = false
    },
    setReceipt: (state, action) => {
      state.receipt = action.payload
      state.loading = false
    },
    setUpdateDescription: (state, action) => {
      state.updateDescription = action.payload
      state.loading = false
    },
    setCheckoutApproveConfirm: (state, action) => {
      state.checkoutApproveConfirmState = action.payload
      state.loading = false
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('checkout/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

export const {
  setLoading,
  setError,
  setExcel,
  setCheckoutList,
  setCheckoutDetail,
  setTransaction,
  setDeleted,
  setCheckoutCreated,
  setCompanyNewCheck,
  setCompanyCheckCheckout,
  setTransactions,
  setReceipt,
  setUpdateDescription,
  setCheckoutApproveConfirm,
  resetState,
} = checkoutSlice.actions

// Selectors
export const selectCheckoutList = (state) => state.checkout.checkout
export const selectCheckoutLoading = (state) => state.checkout.loading
export const selectCheckoutError = (state) => state.checkout.error
export const selectCheckoutDetail = (state) => state.checkout.detail
export const selectCheckoutTransaction = (state) => state.checkout.transaction
export const selectCheckoutCompanyNewCheck = (state) => state.checkout.companyNewCheck
export const selectCheckoutCompanyCheckCheckout = (state) => state.checkout.companyCheckCheckout
export const selectCheckoutTransactions = (state) => state.checkout.transactions
export const selectCheckoutReceipt = (state) => state.checkout.receipt
export const selectCheckoutUpdateDescription = (state) => state.checkout.updateDescription
export const selectCheckoutApproveConfirmState = (state) =>
  state.checkout.checkoutApproveConfirmState

export default checkoutSlice.reducer
